import { updateOrgProfile } from '@/lib/recepthor-api';
import { Button, Field, HelperText, Input, Modal } from '@/ui';
import PropTypes from 'prop-types';
import { useState } from 'react';

import { EMAIL_RE, isCompleteLocal, localFromStored } from '@/lib';
import '@/shared/OrgModales/ModalForm.css';
import PhoneInput from '@/shared/OrgModales/PhoneInput.jsx';

// Edits the organization's DisplayProfile (PUT /api/profile).
export default function EditarPerfilOrgModal({ perfil, onClose, onSaved }) {
  const [form, setForm] = useState({
    legalName: perfil?.legalName ?? '',
    tradeName: perfil?.tradeName ?? '',
    businessActivity: perfil?.businessActivity ?? '',
    contactEmail: perfil?.contactEmail ?? '',
    contactPhone: perfil?.contactPhone ?? '',
    website: perfil?.website ?? '',
  });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const setField = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  function validate() {
    const next = {};
    if (!form.legalName.trim())
      next.legalName = 'La razón social es requerida.';
    if (form.contactEmail.trim() && !EMAIL_RE.test(form.contactEmail.trim()))
      next.contactEmail = 'Email inválido.';
    if (
      form.website.trim() &&
      !/^https?:\/\/\S+\.\S+/.test(form.website.trim())
    )
      next.website = 'URL inválida (incluye https://).';
    const phoneLocal = localFromStored(form.contactPhone);
    if (phoneLocal.length > 0 && !isCompleteLocal(phoneLocal))
      next.contactPhone = 'Teléfono incompleto. Formato: +56 9 1234 1234.';
    return next;
  }

  async function handleSubmit(e) {
    e?.preventDefault();
    setError('');
    const next = validate();
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});

    // contactEmail is not nullable on the API — omit when empty rather than
    // sending ''. Other optional fields accept null to clear them.
    const body = {
      legalName: form.legalName.trim(),
      tradeName: form.tradeName.trim() || null,
      businessActivity: form.businessActivity.trim() || null,
      contactPhone: form.contactPhone.trim() || null,
      website: form.website.trim() || null,
    };
    if (form.contactEmail.trim()) body.contactEmail = form.contactEmail.trim();

    setLoading(true);
    try {
      await updateOrgProfile(body);
      await onSaved?.();
    } catch {
      setError('No se pudo guardar. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal open onClose={onClose}>
      <Modal.Header title="Editar perfil" onClose={onClose} />
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="cfg-form">
            <Field label="Razón social" error={errors.legalName}>
              <Input
                value={form.legalName}
                onChange={setField('legalName')}
                error={!!errors.legalName}
              />
            </Field>
            <Field label="Nombre de fantasía">
              <Input value={form.tradeName} onChange={setField('tradeName')} />
            </Field>
            <Field label="Giro" full>
              <Input
                value={form.businessActivity}
                onChange={setField('businessActivity')}
              />
            </Field>
            <Field label="Email de contacto" error={errors.contactEmail}>
              <Input
                variant="mono"
                value={form.contactEmail}
                onChange={setField('contactEmail')}
                error={!!errors.contactEmail}
                placeholder="contacto@estudio.cl"
              />
            </Field>
            <Field label="Teléfono de contacto" error={errors.contactPhone}>
              <PhoneInput
                id="ep-org-phone"
                value={form.contactPhone}
                onChange={(v) =>
                  setForm((prev) => ({ ...prev, contactPhone: v }))
                }
              />
            </Field>
            <Field label="Sitio web" error={errors.website} full>
              <Input
                variant="mono"
                value={form.website}
                onChange={setField('website')}
                error={!!errors.website}
                placeholder="https://estudio.cl"
              />
            </Field>
            {error && (
              <div className="cfg-form__field--full">
                <HelperText kind="err">{error}</HelperText>
              </div>
            )}
          </div>
          <button
            type="submit"
            aria-hidden="true"
            style={{ display: 'none' }}
          />
        </Modal.Body>
      </form>
      <Modal.Foot>
        <Button
          variant="ghost"
          type="button"
          onClick={onClose}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          variant="seal"
          type="button"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Guardando…' : 'Guardar'}
        </Button>
      </Modal.Foot>
    </Modal>
  );
}

EditarPerfilOrgModal.propTypes = {
  perfil: PropTypes.shape({
    legalName: PropTypes.string,
    tradeName: PropTypes.string,
    businessActivity: PropTypes.string,
    contactEmail: PropTypes.string,
    contactPhone: PropTypes.string,
    website: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
};
